import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import {
  LayoutDashboard,
  Users,
  Package,
  Wrench,
  Calendar,
  UserCog,
  CreditCard,
  BarChart3,
  Bell,
  LogOut,
  DollarSign,
  Briefcase,
  TrendingUp,
  CalendarDays,
  Receipt,
  FileText,
  Clock,
  PieChart,
  Megaphone,
  ShieldCheck,
  UserPlus
} from 'lucide-react';
import CustomersManagement from './admin/CustomersManagement';
import PackagesManagement from './admin/PackagesManagement';
import ServicesManagement from './admin/ServicesManagement';
import EnhancedJobsManagement from './admin/EnhancedJobsManagement';
import HandymenManagement from './admin/HandymenManagement';
import PaymentsManagement from './admin/PaymentsManagement';
import ComprehensiveReports from './admin/ComprehensiveReports';
import LeadsManagement from './admin/LeadsManagement';
import ScheduleCalendar from './admin/ScheduleCalendar';
import ExpensesManagement from './admin/ExpensesManagement';
import EnhancedDashboard from './admin/EnhancedDashboard';
import QuotesManagement from './admin/QuotesManagement';
import InvoiceManagement from './admin/InvoiceManagement';
import TimesheetsManagement from './admin/TimesheetsManagement';
import JobsPnL from './admin/JobsPnL';
import MarketingDashboard from './admin/MarketingDashboard';
import AdminManagement from './admin/AdminManagement';
import NotificationSettings from './admin/NotificationSettings';
import UserManagement from './admin/UserManagement';

export default function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user is primary admin (super_admin or admin have full access)
  const isPrimaryAdmin = user?.is_primary_admin || user?.role === 'super_admin' || user?.role === 'admin';

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  // Base menu items for all admins
  const baseMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: Briefcase, label: 'Leads', path: '/admin/leads' },
    { icon: FileText, label: 'Quotes', path: '/admin/quotes' },
    { icon: Users, label: 'Customers', path: '/admin/customers' },
    { icon: Package, label: 'Packages', path: '/admin/packages' },
    { icon: Wrench, label: 'Services', path: '/admin/services' },
    { icon: Calendar, label: 'Jobs', path: '/admin/jobs' },
    { icon: CalendarDays, label: 'Schedule', path: '/admin/schedule' },
    { icon: UserCog, label: 'Handymen', path: '/admin/handymen' },
    { icon: Clock, label: 'Time Sheets', path: '/admin/timesheets' },
    { icon: PieChart, label: 'Jobs P&L', path: '/admin/jobs-pnl' },
    { icon: Receipt, label: 'Invoices', path: '/admin/invoices' },
    { icon: CreditCard, label: 'Payments', path: '/admin/payments' },
    { icon: DollarSign, label: 'Expenses', path: '/admin/expenses' },
    { icon: Megaphone, label: 'Marketing', path: '/admin/marketing' },
    { icon: Bell, label: 'Notifications', path: '/admin/notifications' },
    { icon: BarChart3, label: 'Reports', path: '/admin/reports' }
  ];

  // Add User Management and Admin Management for primary admin only
  const menuItems = isPrimaryAdmin 
    ? [...baseMenuItems, 
       { icon: UserPlus, label: 'User Management', path: '/admin/users' },
       { icon: ShieldCheck, label: 'Admin Users', path: '/admin/admin-management' }
      ]
    : baseMenuItems;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-white shadow-xl z-10 border-r border-gray-200 flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Redefyne
          </h1>
          <p className="text-sm text-gray-500 mt-1">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gray-200 text-[#595959] font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <div className="mb-3 px-4">
            <p className="text-sm font-medium text-gray-900">{user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#595959] text-white p-4 z-50 shadow-lg safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Redefyne</h1>
            <p className="text-sm opacity-90">Admin Panel</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/20">
            <LogOut size={18} />
          </Button>
        </div>
      </div>

      {/* Main Content - Responsive padding */}
      <main className="md:ml-64 p-4 md:p-8 pt-20 md:pt-8 pb-24 md:pb-8">
        <Routes>
          <Route
            path="/"
            element={<EnhancedDashboard />}
          />
          <Route path="/leads" element={<LeadsManagement />} />
          <Route path="/quotes" element={<QuotesManagement />} />
          <Route path="/customers" element={<CustomersManagement />} />
          <Route path="/packages" element={<PackagesManagement />} />
          <Route path="/services" element={<ServicesManagement />} />
          <Route path="/jobs" element={<EnhancedJobsManagement />} />
          <Route path="/schedule" element={<ScheduleCalendar />} />
          <Route path="/handymen" element={<HandymenManagement />} />
          <Route path="/timesheets" element={<TimesheetsManagement />} />
          <Route path="/jobs-pnl" element={<JobsPnL />} />
          <Route path="/invoices" element={<InvoiceManagement />} />
          <Route path="/payments" element={<PaymentsManagement />} />
          <Route path="/expenses" element={<ExpensesManagement />} />
          <Route path="/marketing" element={<MarketingDashboard />} />
          <Route path="/notifications" element={<NotificationSettings />} />
          <Route path="/reports" element={<ComprehensiveReports />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/admin-management" element={<AdminManagement currentUser={user} />} />
        </Routes>
      </main>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50 safe-area-inset-bottom">
        <div className="grid grid-cols-5 gap-1 p-2 overflow-x-auto">
          <Link
            to="/admin"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/admin' ? 'bg-gray-200 text-[#595959]' : 'text-gray-600'
            }`}
          >
            <LayoutDashboard size={20} />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link
            to="/admin/leads"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/admin/leads' ? 'bg-gray-200 text-[#595959]' : 'text-gray-600'
            }`}
          >
            <Briefcase size={20} />
            <span className="text-xs mt-1">Leads</span>
          </Link>
          <Link
            to="/admin/jobs"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/admin/jobs' ? 'bg-gray-200 text-[#595959]' : 'text-gray-600'
            }`}
          >
            <Calendar size={20} />
            <span className="text-xs mt-1">Jobs</span>
          </Link>
          <Link
            to="/admin/customers"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/admin/customers' ? 'bg-gray-200 text-[#595959]' : 'text-gray-600'
            }`}
          >
            <Users size={20} />
            <span className="text-xs mt-1">Clients</span>
          </Link>
          <Link
            to="/admin/marketing"
            className={`flex flex-col items-center py-2 px-1 rounded-lg transition-all ${
              location.pathname === '/admin/marketing' ? 'bg-gray-200 text-[#595959]' : 'text-gray-600'
            }`}
          >
            <Megaphone size={20} />
            <span className="text-xs mt-1">Market</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
